/**
 * "Fast årlig innkalling" (annual recall) — step logic for
 * partials/annual-recall-lightbox.php; submits to inc/annual-recall.php.
 *
 * Steps: 1 (clinic) → 2 (details) → review → done. The footer button is
 * "Next" / "Send form" / "Close" and is disabled until the current step is
 * valid. A × on the review step jumps back to that value's step. The form
 * resets whenever the lightbox is closed after a successful send.
 *
 * Besides its own [data-lightbox-open="annual-recall"] triggers, the
 * lightbox opens for any link whose URL ends in #fast-innkalling (custom
 * menu links, buttons in content…), and on page load when the address
 * already carries that hash.
 */

import { openLightbox } from '@/js/lightbox.js';

const HASH = '#fast-innkalling';

const STEPS = [ '1', '2', 'review', 'done' ];

function initAnnualRecall( root ) {
	const form = root.querySelector( 'form' );
	const next = root.querySelector( '[data-ar-next]' );
	const intro = root.querySelector( '[data-ar-intro]' );
	const progress = root.querySelector( '[data-ar-progress]' );
	const bar = root.querySelector( '[data-ar-bar]' );
	const stepNo = root.querySelector( '[data-ar-step-no]' );
	const picker = root.querySelector( '[data-ar-picker]' );
	const chosen = root.querySelector( '[data-ar-chosen]' );
	const chosenLabel = root.querySelector( '[data-ar-chosen-label]' );
	const phoneError = root.querySelector( '[data-ar-phone-error]' );
	const errorBox = root.querySelector( '[data-ar-error]' );
	const panels = Object.fromEntries( STEPS.map( ( s ) => [ s, root.querySelector( `[data-ar-step="${ s }"]` ) ] ) );
	const introDefault = intro.textContent;

	const field = ( name ) => form.elements.namedItem( name );
	let step = '1';
	let sending = false;

	const digits = ( value ) => value.replace( /\D+/g, '' );
	const phoneValid = () => digits( field( 'phone' ).value ).length === 8;
	const clinicInput = () => form.querySelector( 'input[name="clinic"]:checked' );

	const stepValid = () => {
		switch ( step ) {
			case '1':
				return !! clinicInput();
			case '2':
				return field( 'first_name' ).value.trim() !== ''
					&& field( 'last_name' ).value.trim() !== ''
					&& phoneValid()
					&& field( 'month' ).value !== '';
			case 'review':
				return field( 'consent' ).checked && ! sending;
			default:
				return true;
		}
	};

	const updateButton = () => {
		const label = step === 'review' ? 'labelSend' : step === 'done' ? 'labelClose' : 'labelNext';
		next.textContent = next.dataset[ label ];
		// The final "Back to the website" button is fersken, not the dark primary.
		next.classList.toggle( 'btn-primary', step !== 'done' );
		next.classList.toggle( 'btn-emergency', step === 'done' );
		next.disabled = ! stepValid();
	};

	const fillReview = () => {
		const month = field( 'month' );
		const values = {
			clinic: clinicInput()?.dataset.label || '',
			first_name: field( 'first_name' ).value.trim(),
			last_name: field( 'last_name' ).value.trim(),
			phone: field( 'phone' ).value.trim(),
			month: month.value ? month.options[ month.selectedIndex ].text : '',
		};
		Object.entries( values ).forEach( ( [ key, value ] ) => {
			root.querySelector( `[data-ar-review="${ key }"]` ).textContent = value;
			root.querySelector( `[data-ar-review-row="${ key }"]` ).classList.toggle( 'hidden', ! value );
		} );
	};

	const show = ( target ) => {
		step = target;
		STEPS.forEach( ( s ) => {
			panels[ s ].classList.toggle( 'hidden', s !== step );
			panels[ s ].classList.toggle( 'flex', s === step && s === '2' );
		} );
		progress.classList.toggle( 'hidden', step === 'review' || step === 'done' );
		stepNo.textContent = step === '2' ? '2' : '1';
		bar.style.width = step === '2' ? '100%' : '50%';
		intro.textContent = step === 'review' ? intro.dataset.introReview : introDefault;
		// The thank-you screen shows only its own heading + line.
		intro.classList.toggle( 'hidden', step === 'done' );
		root.querySelector( '[data-ar-title]' ).classList.toggle( 'hidden', step === 'done' );
		errorBox.classList.add( 'hidden' );
		if ( step === 'review' ) {
			fillReview();
		}
		updateButton();
		root.scrollTop = 0; // the lightbox overlay is the scroller
	};

	// Step 1: picking a clinic collapses the tree to "<clinic> ×".
	const syncClinic = () => {
		const input = clinicInput();
		picker.classList.toggle( 'hidden', !! input );
		chosen.classList.toggle( 'hidden', ! input );
		chosen.classList.toggle( 'flex', !! input );
		chosenLabel.textContent = input ? input.dataset.label : '';
		updateButton();
	};

	const clearClinic = () => {
		const input = clinicInput();
		if ( input ) {
			input.checked = false;
		}
		picker.open = true;
		syncClinic();
	};

	form.addEventListener( 'change', ( event ) => {
		if ( event.target.name === 'clinic' ) {
			syncClinic();
			return;
		}
		updateButton();
	} );

	form.addEventListener( 'input', ( event ) => {
		if ( event.target.name === 'phone' ) {
			// Only flag once the visitor has typed enough to be judged.
			const bad = event.target.value.trim() !== '' && ! phoneValid() && digits( event.target.value ).length >= 8;
			event.target.setAttribute( 'aria-invalid', String( bad ) );
			phoneError.classList.toggle( 'hidden', ! bad );
		}
		updateButton();
	} );

	form.querySelector( 'input[name="phone"]' ).addEventListener( 'blur', ( event ) => {
		const bad = event.target.value.trim() !== '' && ! phoneValid();
		event.target.setAttribute( 'aria-invalid', String( bad ) );
		phoneError.classList.toggle( 'hidden', ! bad );
	} );

	root.querySelector( '[data-ar-clear-clinic]' ).addEventListener( 'click', clearClinic );

	root.querySelectorAll( '[data-ar-edit]' ).forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			const key = btn.dataset.arEdit;
			if ( key === 'clinic' ) {
				clearClinic();
				show( '1' );
				return;
			}
			show( '2' );
			const input = field( key );
			if ( input ) {
				input.value = '';
				updateButton();
				input.focus();
			}
		} );
	} );

	const submit = async () => {
		sending = true;
		updateButton();
		errorBox.classList.add( 'hidden' );

		const payload = {
			clinic: clinicInput()?.value,
			first_name: field( 'first_name' ).value.trim(),
			last_name: field( 'last_name' ).value.trim(),
			phone: digits( field( 'phone' ).value ),
			month: field( 'month' ).value,
			new_patient: field( 'new_patient' ).checked,
			consent: field( 'consent' ).checked,
			website: field( 'website' ).value,
		};

		try {
			const response = await fetch( root.dataset.endpoint, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify( payload ),
				credentials: 'same-origin',
			} );
			const data = await response.json().catch( () => ( {} ) );
			if ( ! response.ok || ! data.ok ) {
				throw new Error( data.message || root.dataset.errorGeneric );
			}
			sending = false;
			show( 'done' );
		} catch ( err ) {
			sending = false;
			errorBox.textContent = err.message || root.dataset.errorGeneric;
			errorBox.classList.remove( 'hidden' );
			updateButton();
		}
	};

	next.addEventListener( 'click', () => {
		if ( next.disabled ) {
			return;
		}
		if ( step === '1' ) {
			show( '2' );
		} else if ( step === '2' ) {
			show( 'review' );
		} else if ( step === 'review' ) {
			submit();
		} else {
			root.querySelector( '[data-lightbox-close]' )?.click();
		}
	} );

	// After a successful send, start over the next time the lightbox opens.
	new MutationObserver( () => {
		if ( ! root.classList.contains( 'is-open' ) && step === 'done' ) {
			form.reset();
			field( 'phone' ).setAttribute( 'aria-invalid', 'false' );
			phoneError.classList.add( 'hidden' );
			clearClinic();
			show( '1' );
		}
	} ).observe( root, { attributes: true, attributeFilter: [ 'class' ] } );

	show( '1' );
	syncClinic();
}

document.querySelectorAll( '[data-annual-recall]' ).forEach( initAnnualRecall );

const annualRecall = document.getElementById( 'annual-recall' );

if ( annualRecall ) {
	document.addEventListener( 'click', ( event ) => {
		const link = event.target.closest( 'a[href]' );
		if ( link && link.hash === HASH ) {
			event.preventDefault();
			openLightbox( annualRecall, link );
		}
	} );

	if ( window.location.hash === HASH ) {
		history.replaceState( null, '', window.location.pathname + window.location.search );
		openLightbox( annualRecall );
	}
}
